import { message } from 'antd'
import { RevisionOption } from 'features/reporting/types'
import { VisualInspectionFormDataState } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useCallback, useState } from 'react'
import { GeckoReportingMetadata } from 'types'

import { getSelectedFormData } from './useFormData'

// Visual report specific section selection
export interface VisualSectionSelection {
  coverPage: boolean
  checklistFindings: boolean
  assetCorrosionAnalysis: boolean
  findings: boolean
  recommendations: boolean
  pressureTestSubInspections: boolean
  bundleSubInspections: boolean
  inspectionTeam: boolean
  referenceDocuments: boolean
}

interface UseReportRevisionsProps {
  metadataEntries: readonly GeckoReportingMetadata[]
  currentRevisionNumber: number
  revisions: RevisionOption[]
  onUpdateFormData: (data: Partial<VisualInspectionFormDataState>) => void
}

interface UseReportRevisionsResult {
  selectedRevisionData?: VisualInspectionFormDataState
  loading: boolean
  loadRevisionData: (revisionId: string) => Promise<void>
  copyRevisionContent: (sections: VisualSectionSelection) => Promise<void>
  clearSelectedRevision: () => void
  hasPublishedRevisions: boolean
  canRevertToCantilever: boolean
}

export const useReportRevisions = ({
  metadataEntries,
  currentRevisionNumber,
  revisions,
  onUpdateFormData,
}: UseReportRevisionsProps): UseReportRevisionsResult => {
  const [selectedRevisionData, setSelectedRevisionData] =
    useState<VisualInspectionFormDataState>()
  const [selectedRevisionId, setSelectedRevisionId] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [copyingContent, setCopyingContent] = useState(false)

  const loadRevisionData = useCallback(
    async (revisionId: string) => {
      setLoading(true)
      try {
        const revisionData = getSelectedFormData(revisionId, metadataEntries)

        if (revisionData) {
          setSelectedRevisionData(revisionData)
          setSelectedRevisionId(revisionId)
          message.success('Revision data loaded successfully')
        } else {
          throw new Error('No revision data found')
        }
      } catch (error) {
        console.error('Failed to load revision data:', error)
        message.error('Failed to load revision data')
        setSelectedRevisionData(undefined)
        setSelectedRevisionId('')
      } finally {
        setLoading(false)
      }
    },
    [metadataEntries]
  )

  // Copy revision content function
  const copyRevisionContent = useCallback(
    async (sections: VisualSectionSelection) => {
      if (!selectedRevisionData || !selectedRevisionId) {
        message.error('No revision data selected')
        return
      }

      setCopyingContent(true)
      try {
        const contentToCopy: Partial<VisualInspectionFormDataState> = {}

        // Copy basic content based on sections
        if (sections.coverPage) {
          contentToCopy.coverSectionData = selectedRevisionData.coverSectionData
          contentToCopy.mainInfo = selectedRevisionData.mainInfo
        }

        if (sections.checklistFindings) {
          contentToCopy.checklistFindingsData =
            selectedRevisionData.checklistFindingsData
        }

        if (sections.assetCorrosionAnalysis) {
          contentToCopy.assetCorrosionAnalysisData =
            selectedRevisionData.assetCorrosionAnalysisData
        }

        if (sections.findings) {
          contentToCopy.findingsData = selectedRevisionData.findingsData
        }

        if (sections.recommendations) {
          contentToCopy.recommendationsData =
            selectedRevisionData.recommendationsData
        }

        if (sections.pressureTestSubInspections) {
          contentToCopy.pressureTestSubInspectionsData =
            selectedRevisionData.pressureTestSubInspectionsData
        }

        if (sections.bundleSubInspections) {
          contentToCopy.bundleSubInspectionsData =
            selectedRevisionData.bundleSubInspectionsData
        }

        if (sections.inspectionTeam) {
          contentToCopy.inspectionTeamMemberData =
            selectedRevisionData.inspectionTeamMemberData
        }

        // Handle file-based content
        if (sections.referenceDocuments) {
          // Copy the reference documents data
          contentToCopy.referenceDocumentsData =
            selectedRevisionData.referenceDocumentsData
        }

        await onUpdateFormData(contentToCopy)
        message.success('Successfully copied content from past revision')
      } catch (error) {
        console.error('Failed to copy revision content:', error)
        message.error('Failed to copy some content from past revision')
      } finally {
        setCopyingContent(false)
      }
    },
    [selectedRevisionData, selectedRevisionId, onUpdateFormData]
  )

  const clearSelectedRevision = useCallback(() => {
    setSelectedRevisionData(undefined)
    setSelectedRevisionId('')
  }, [])

  const hasPublishedRevisions = revisions.some(
    (rev) => rev.isPublished && rev.revisionNumber < currentRevisionNumber
  )

  const canRevertToCantilever = Boolean(
    revisions.find((rev) => rev.revisionNumber === currentRevisionNumber)
      ?.isPublished === false
  )

  return {
    selectedRevisionData,
    loading: loading || copyingContent,
    loadRevisionData,
    copyRevisionContent,
    clearSelectedRevision,
    hasPublishedRevisions,
    canRevertToCantilever,
  }
}
